import type { CrashSession } from "./log";

const REPORTS_URL = "https://reports.yarn.watch/";
const ENABLED_KEY = "yarn-crash-send";
const SENT_KEY = "yarn-crash-sent";
const KEPT_SENT = 20;

type SentStorage = Pick<Storage, "getItem" | "setItem">;

function readSent(storage: SentStorage): string[] {
  try {
    return JSON.parse(storage.getItem(SENT_KEY) ?? "[]") as string[];
  } catch {
    return [];
  }
}

/** Off unless someone turned it on for this device, on the status page. */
export function sendingEnabled(storage: Pick<Storage, "getItem">): boolean {
  return storage.getItem(ENABLED_KEY) === "1";
}

export function setSendingEnabled(
  storage: Pick<Storage, "setItem" | "removeItem">,
  enabled: boolean,
): void {
  if (enabled) {
    storage.setItem(ENABLED_KEY, "1");
  } else {
    storage.removeItem(ENABLED_KEY);
  }
}

/** Sends whatever this device has not sent yet, if it has been turned on here. */
export function sendPendingReports(
  reports: readonly CrashSession[],
  storage: SentStorage,
): Promise<void> {
  if (!import.meta.env.PROD || !sendingEnabled(storage)) {
    return Promise.resolve();
  }
  return sendCrashReports(reports, storage, (body) => fetch(REPORTS_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
  }));
}

/** Sends each crash once. Only what the crash log holds: no name, account, or address. */
export async function sendCrashReports(
  reports: readonly CrashSession[],
  storage: SentStorage,
  post: (report: string) => Promise<{ ok: boolean }>,
): Promise<void> {
  const sent = readSent(storage);
  for (const report of reports) {
    if (sent.includes(report.startedAt)) {
      continue;
    }
    try {
      if ((await post(JSON.stringify(report))).ok) {
        sent.push(report.startedAt);
        storage.setItem(SENT_KEY, JSON.stringify(sent.slice(-KEPT_SENT)));
      }
    } catch {
      // No connection, or a full disk. It stays unsent and goes next time.
    }
  }
}
